// src/context/ThemeContext.js
import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// ====== Tema Clean (Minimalista e Profissional) ======
const cleanTheme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#F9F9F9',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#333333',
      secondary: '#666666'
    },
    primary: {
      main: '#007BFF', // azul confiança
    },
    success: { main: '#4CAF50' },
    error: { main: '#F44336' },
    warning: { main: '#FFC107' }
  },
  typography: {
    fontFamily: 'Poppins, Inter, sans-serif',
    h6: { fontWeight: 700 },
    body2: { color: '#666' }
  },
  shape: { borderRadius: 12 },
  shadows: Array(25).fill('0px 2px 6px rgba(0,0,0,0.08)'),
});

// ====== Tema Dark Neon (Futurista e Imersivo) ======
const darkNeonTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1D1D1D',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#CCCCCC'
    },
    primary: { main: '#00E5FF' }, // Neon ciano
    success: { main: '#BFFF00' },
    error: { main: '#FF00FF' },
    warning: { main: '#FDFD96' }
  },
  typography: {
    fontFamily: 'Poppins, Montserrat, sans-serif',
    h5: {
      fontWeight: 800,
      textShadow: '0 0 8px #00E5FF'
    }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #00E5FF',
          boxShadow: '0 0 8px #00E5FF55'
        }
      }
    }
  }
});

// ====== Contexto ======
const ThemeModeContext = createContext({
  mode: 'light',
  setMode: () => {},
  toggleTheme: () => {}
});

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('mui-mode');
    if (saved === 'light' || saved === 'dark') return saved;
    // detecta preferência do SO
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('mui-mode', next);
      return next;
    });
  };

  const theme = useMemo(() => (mode === 'light' ? cleanTheme : darkNeonTheme), [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    console.warn('⚠️ useThemeMode chamado fora do ThemeModeProvider. Retornando valores padrão.');
    return {
      mode: 'light',
      setMode: () => {},
      toggleTheme: () => {}
    };
  }
  return context;
};
